import { formatDate } from "@clipboard-health/date-time";
import { CardWithIntent, LoadingSpinner, Text } from "@clipboard-health/ui-components";
import { type IntentVariant } from "@clipboard-health/ui-theme";
import { isDefined } from "@clipboard-health/util-ts";
import { CardContent, Stack, useTheme } from "@mui/material";
import { MuiMarkdown } from "@src/appV2/lib";
import { differenceInDays } from "date-fns";
import { type ReactNode } from "react";

import { DocumentStatusPill } from "./StatusPill";

export const DOCUMENT_STATUS_CARD_VARIANTS = [
  "expiring_soon",
  "rejected",
  "expired",
  "approved",
  "in_review",
] as const;

export type DocumentStatusCardVariant = (typeof DOCUMENT_STATUS_CARD_VARIANTS)[number];

export interface DocumentStatusCardProps {
  variant: DocumentStatusCardVariant;
  title?: string;
  statusMessage?: string;
  expirationDate?: Date;
  isLoading?: boolean;
  children?: ReactNode;
  pillLabel?: string;
}

const VARIANT_TITLE_MAP: Record<Exclude<DocumentStatusCardVariant, "expiring_soon">, string> = {
  rejected: "Please complete this document again",
  expired: "This document is expired",
  approved: "This document has been approved",
  in_review: "This document is in review",
};

const VARIANT_DESCRIPTION_MAP: Record<
  "approved" | "in_review" | "expired" | "rejected",
  string | undefined
> = {
  approved: undefined,
  in_review:
    "Document reviews are usually completed within an hour. We'll notify you once it's done.",
  expired:
    "To continue booking shifts without interruption, please complete this requirement again.",
  rejected: undefined,
};

function getVariantTitle(
  variant: DocumentStatusCardVariant,
  expirationDate?: Date,
  title?: string
) {
  if (isDefined(title)) {
    return title;
  }

  if (variant === "expiring_soon") {
    if (isDefined(expirationDate)) {
      const daysUntilExpiry = differenceInDays(expirationDate, new Date());
      if (daysUntilExpiry < 0) {
        return "This document is expiring soon";
      }

      return `This document is expiring ${
        daysUntilExpiry === 0 ? "today" : `in ${daysUntilExpiry} days`
      }`;
    }

    return "This document is expiring soon";
  }

  return VARIANT_TITLE_MAP[variant];
}

function getVariantDescription(
  variant: DocumentStatusCardVariant,
  expirationDate?: Date,
  statusMessage?: string
) {
  if (isDefined(statusMessage)) {
    return statusMessage;
  }

  if (variant === "expiring_soon") {
    if (isDefined(expirationDate)) {
      return `To continue booking shifts without interruption, please upload a new document by ${formatDate(
        expirationDate
      )}.`;
    }

    return "To continue booking shifts without interruption, please complete this requirement again.";
  }

  return VARIANT_DESCRIPTION_MAP[variant];
}

const CARD_INTENT_MAP: Record<DocumentStatusCardVariant, IntentVariant> = {
  expiring_soon: "warning",
  rejected: "error",
  expired: "error",
  approved: "success",
  in_review: "neutral",
};

export function DocumentStatusCard(props: DocumentStatusCardProps) {
  const { variant, expirationDate, statusMessage, isLoading, children, pillLabel } = props;

  const title = getVariantTitle(variant, expirationDate, props.title);
  const description = getVariantDescription(variant, expirationDate, statusMessage);
  const theme = useTheme();

  return (
    <CardWithIntent intent={CARD_INTENT_MAP[variant]} sx={{ flexShrink: 0 }}>
      <CardContent>
        {/* Different spacing for approved variant since it has no description */}
        <Stack direction="column" spacing={variant === "approved" ? 5 : 7}>
          <Stack direction="column" spacing={5}>
            <Stack direction="row" spacing={3} alignItems="center" justifyContent="space-between">
              <DocumentStatusPill
                variant={variant}
                size="small"
                expiry={expirationDate}
                label={pillLabel}
              />

              {isLoading && <LoadingSpinner size="small" />}
            </Stack>

            <Text semibold variant="h5">
              {title}
            </Text>

            {/* The status message could be in Markdown format */}
            {isDefined(description) && (
              <MuiMarkdown color={theme.palette.text.secondary} variant="body1">
                {description}
              </MuiMarkdown>
            )}
          </Stack>

          {children}
        </Stack>
      </CardContent>
    </CardWithIntent>
  );
}
